import { Download, Edit3, FileOutput, FileText, Mail } from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import { FormField, FormInput } from '@/components/ui/FormField';
import { printHtmlPreview } from '@/hooks/use-pdf-export';
import { api } from '@/lib/api';
import { toast } from '@/stores/toast-store';

interface Props {
  loanId: string;
  loanNumber: string;
  extensions?: { id: string; extensionNumber: number }[];
  settlements?: { id: string; statementNumber: string }[];
}

interface DocumentSelection {
  type: string;
  extraId?: string;
}

type OverrideField =
  | 'borrowerName'
  | 'loanId'
  | 'loanAmount'
  | 'interestRate'
  | 'term'
  | 'startDate'
  | 'maturityDate'
  | 'additionalNotes';

type OverrideState = Record<OverrideField, string>;
type OverrideErrors = Partial<Record<OverrideField, string>>;

const EMPTY_OVERRIDES: OverrideState = {
  borrowerName: '',
  loanId: '',
  loanAmount: '',
  interestRate: '',
  term: '',
  startDate: '',
  maturityDate: '',
  additionalNotes: '',
};

const NAME_MAX_LENGTH = 200;
const LOAN_ID_MAX_LENGTH = 100;
const NOTES_MAX_LENGTH = 2000;
const INTEREST_RATE_MAX = 100;
const TERM_MAX_MONTHS = 360;
const LOAN_ID_PATTERN = /^[A-Za-z0-9\s\-_/.:#()]+$/;
const CURRENCY_PATTERN = /^\$?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d{1,2})?$|^\$?\.\d{1,2}$/;
const NUMBER_PATTERN = /^(?:\d+|\d+\.\d+|\.\d+)$/;
const INTEGER_PATTERN = /^\d+$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isValidIsoDate(value: string): boolean {
  if (!ISO_DATE_PATTERN.test(value)) {
    return false;
  }

  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

function validateOverrides(overrides: OverrideState): OverrideErrors {
  const errors: OverrideErrors = {};
  const borrowerName = overrides.borrowerName.trim();
  const loanId = overrides.loanId.trim();
  const loanAmount = overrides.loanAmount.trim();
  const interestRate = overrides.interestRate.trim();
  const term = overrides.term.trim();
  const startDate = overrides.startDate.trim();
  const maturityDate = overrides.maturityDate.trim();
  const additionalNotes = overrides.additionalNotes.trim();

  if (borrowerName && borrowerName.length > NAME_MAX_LENGTH) {
    errors.borrowerName = `Borrower Name must be at most ${NAME_MAX_LENGTH} characters`;
  }

  if (loanId) {
    if (loanId.length > LOAN_ID_MAX_LENGTH) {
      errors.loanId = `Loan ID must be at most ${LOAN_ID_MAX_LENGTH} characters`;
    } else if (!LOAN_ID_PATTERN.test(loanId)) {
      errors.loanId = 'Loan ID contains unsupported characters';
    }
  }

  if (loanAmount) {
    if (!CURRENCY_PATTERN.test(loanAmount)) {
      errors.loanAmount = 'Loan Amount must be a valid positive number';
    } else if (Number.parseFloat(loanAmount.replace(/[$,]/g, '')) <= 0) {
      errors.loanAmount = 'Loan Amount must be greater than 0';
    }
  }

  if (interestRate) {
    if (!NUMBER_PATTERN.test(interestRate)) {
      errors.interestRate = 'Interest Rate must be a valid number';
    } else {
      const rate = Number.parseFloat(interestRate);
      if (rate < 0) {
        errors.interestRate = 'Interest Rate cannot be negative';
      } else if (rate > INTEREST_RATE_MAX) {
        errors.interestRate = `Interest Rate cannot exceed ${INTEREST_RATE_MAX}%`;
      }
    }
  }

  if (term) {
    if (!INTEGER_PATTERN.test(term)) {
      errors.term = 'Term must be a whole number of months';
    } else {
      const months = Number.parseInt(term, 10);
      if (months < 1) {
        errors.term = 'Term must be at least 1 month';
      } else if (months > TERM_MAX_MONTHS) {
        errors.term = `Term cannot exceed ${TERM_MAX_MONTHS} months`;
      }
    }
  }

  if (startDate && !isValidIsoDate(startDate)) {
    errors.startDate = 'Start Date must be a valid date';
  }

  if (maturityDate && !isValidIsoDate(maturityDate)) {
    errors.maturityDate = 'Maturity Date must be a valid date';
  }

  if (additionalNotes.length > NOTES_MAX_LENGTH) {
    errors.additionalNotes = `Additional Notes must be at most ${NOTES_MAX_LENGTH} characters`;
  }

  return errors;
}

export function DocumentGeneratorPanel({
  loanId,
  loanNumber,
  extensions = [],
  settlements = [],
}: Props) {
  const [generating, setGenerating] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [originalHtml, setOriginalHtml] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentSelection | null>(null);
  const [applyingChanges, setApplyingChanges] = useState(false);
  const [overrides, setOverrides] = useState<OverrideState>(EMPTY_OVERRIDES);
  const errors = validateOverrides(overrides);
  const isFormValid = Object.keys(errors).length === 0;

  function getLoadingKey(type: string, extraId?: string) {
    return extraId ? `${type}:${extraId}` : type;
  }

  function getErrorMessage(error: any, fallback: string) {
    const responseData = error?.response?.data;
    if (typeof responseData === 'string' && responseData.trim()) {
      try {
        const parsed = JSON.parse(responseData);
        if (parsed?.message) {
          return String(parsed.message);
        }
      } catch {
        return responseData;
      }
    }

    if (responseData?.message) {
      return String(responseData.message);
    }

    return error?.message || fallback;
  }

  async function generate(type: string, extraId?: string, loadingKey?: string) {
    setGenerating(loadingKey ?? getLoadingKey(type, extraId));
    try {
      let url = `/documents/generate/${loanId}/${type}`;
      if (extraId) url += `/${extraId}`;
      const { data } = await api.get(url, { responseType: 'text' });
      setPreviewHtml(data);
      setOriginalHtml(data);
      setSelectedDocument({ type, extraId });
      setPreviewTitle(type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
      setEditMode(false);
      setOverrides(EMPTY_OVERRIDES);
    } catch (e: any) {
      const status = e?.response?.status;
      const msg = getErrorMessage(e, 'Failed to generate document');
      if (status === 429) {
        toast.error('Too many requests — please wait a moment then try again');
      } else {
        toast.error(`Failed to generate document${msg ? `: ${msg}` : ''}`);
      }
    } finally {
      setGenerating(null);
    }
  }

  function getStructuredOverrides() {
    return Object.fromEntries(
      Object.entries(overrides)
        .map(([key, value]) => [key, value.trim()])
        .filter(([, value]) => value !== ''),
    );
  }

  async function handleApplyChanges() {
    if (!selectedDocument || !isFormValid) return;

    setApplyingChanges(true);
    try {
      let url = `/documents/generate/preview/${loanId}/${selectedDocument.type}`;
      if (selectedDocument.extraId) {
        url += `/${selectedDocument.extraId}`;
      }

      const { data } = await api.post(url, getStructuredOverrides(), {
        responseType: 'text',
      });
      setPreviewHtml(data);
      setEditMode(false);
    } catch (e: any) {
      const msg = getErrorMessage(e, 'Failed to apply document changes');
      toast.error(`Failed to apply document changes${msg ? `: ${msg}` : ''}`);
    } finally {
      setApplyingChanges(false);
    }
  }

  function handleResetEdits() {
    if (originalHtml) {
      setPreviewHtml(originalHtml);
      setEditMode(false);
      setOverrides(EMPTY_OVERRIDES);
    }
  }

  function handlePrint() {
    if (!previewHtml) return;
    printHtmlPreview(previewHtml);
  }

  function handleOverrideChange(field: OverrideField, value: string) {
    setOverrides(prev => ({
      ...prev,
      [field]: value,
    }));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-3">
        <FileOutput className="w-4 h-4 text-indigo-500" />
        <h3 className="text-sm font-semibold text-gray-900">Document Generation</h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <DocButton
          label="Offer Letter"
          icon={FileText}
          loading={generating === getLoadingKey('offer-letter')}
          onClick={() => generate('offer-letter', undefined, getLoadingKey('offer-letter'))}
        />
        <DocButton
          label="Discharge Letter"
          icon={Mail}
          loading={generating === getLoadingKey('discharge-letter')}
          onClick={() => generate('discharge-letter', undefined, getLoadingKey('discharge-letter'))}
        />
        {extensions.map(ext => (
          <DocButton
            key={ext.id}
            label={`Extension #${ext.extensionNumber}`}
            icon={FileText}
            loading={generating === getLoadingKey('extension-letter', ext.id)}
            onClick={() =>
              generate('extension-letter', ext.id, getLoadingKey('extension-letter', ext.id))
            }
          />
        ))}
        {settlements.map(s => (
          <DocButton
            key={s.id}
            label={`Settlement ${s.statementNumber}`}
            icon={FileText}
            loading={generating === getLoadingKey('settlement-statement', s.id)}
            onClick={() =>
              generate('settlement-statement', s.id, getLoadingKey('settlement-statement', s.id))
            }
          />
        ))}
      </div>

      {/* Preview */}
      {previewHtml && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-700">
              {previewTitle} — {loanNumber}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setEditMode(!editMode)}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700"
              >
                <Edit3 className="w-3 h-3" /> {editMode ? 'Cancel' : 'Edit Document'}
              </button>
              <Button
                variant="primary"
                onClick={handlePrint}
                className="flex items-center gap-1 px-2 py-1 text-xs text-white rounded"
              >
                <Download className="w-3 h-3" /> Print / Save PDF
              </Button>
              <button
                onClick={() => setPreviewHtml(null)}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
          </div>

          {/* Edit Panel */}
          {editMode && (
            <div className="px-4 py-4 bg-white border-b border-gray-200 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FormField
                  label="Borrower Name"
                  error={errors.borrowerName}
                  htmlFor="document-borrower-name"
                >
                  <FormInput
                    id="document-borrower-name"
                    type="text"
                    value={overrides.borrowerName}
                    onChange={e => handleOverrideChange('borrowerName', e.target.value)}
                    placeholder="Enter borrower name"
                    error={errors.borrowerName}
                    maxLength={NAME_MAX_LENGTH}
                  />
                </FormField>
                <FormField label="Loan ID" error={errors.loanId} htmlFor="document-loan-id">
                  <FormInput
                    id="document-loan-id"
                    type="text"
                    value={overrides.loanId}
                    onChange={e => handleOverrideChange('loanId', e.target.value)}
                    placeholder="Enter loan ID"
                    error={errors.loanId}
                    maxLength={LOAN_ID_MAX_LENGTH}
                  />
                </FormField>
                <FormField
                  label="Loan Amount"
                  error={errors.loanAmount}
                  htmlFor="document-loan-amount"
                >
                  <FormInput
                    id="document-loan-amount"
                    type="text"
                    inputMode="decimal"
                    value={overrides.loanAmount}
                    onChange={e => handleOverrideChange('loanAmount', e.target.value)}
                    placeholder="e.g., $50,000.00"
                    error={errors.loanAmount}
                  />
                </FormField>
                <FormField
                  label="Interest Rate"
                  error={errors.interestRate}
                  htmlFor="document-interest-rate"
                >
                  <FormInput
                    id="document-interest-rate"
                    type="text"
                    inputMode="decimal"
                    value={overrides.interestRate}
                    onChange={e => handleOverrideChange('interestRate', e.target.value)}
                    placeholder="e.g., 5.5"
                    error={errors.interestRate}
                  />
                </FormField>
                <FormField label="Term" error={errors.term} htmlFor="document-term">
                  <FormInput
                    id="document-term"
                    type="text"
                    inputMode="numeric"
                    value={overrides.term}
                    onChange={e => handleOverrideChange('term', e.target.value)}
                    placeholder="e.g., 24"
                    error={errors.term}
                  />
                </FormField>
                <FormField
                  label="Start Date"
                  error={errors.startDate}
                  htmlFor="document-start-date"
                >
                  <FormInput
                    id="document-start-date"
                    type="date"
                    value={overrides.startDate}
                    onChange={e => handleOverrideChange('startDate', e.target.value)}
                    error={errors.startDate}
                  />
                </FormField>
                <FormField
                  label="Maturity Date"
                  error={errors.maturityDate}
                  htmlFor="document-maturity-date"
                >
                  <FormInput
                    id="document-maturity-date"
                    type="date"
                    value={overrides.maturityDate}
                    onChange={e => handleOverrideChange('maturityDate', e.target.value)}
                    error={errors.maturityDate}
                  />
                </FormField>
              </div>
              <FormField
                label="Additional Notes"
                error={errors.additionalNotes}
                htmlFor="document-additional-notes"
              >
                <textarea
                  id="document-additional-notes"
                  value={overrides.additionalNotes}
                  onChange={e => handleOverrideChange('additionalNotes', e.target.value)}
                  placeholder="Enter any additional notes to append to the document"
                  maxLength={NOTES_MAX_LENGTH}
                  aria-invalid={!!errors.additionalNotes}
                  aria-describedby={
                    errors.additionalNotes ? 'document-additional-notes-error' : undefined
                  }
                  className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 transition-colors ${
                    errors.additionalNotes
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50/50'
                      : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                  }`}
                  rows={3}
                />
              </FormField>
              <div className="flex items-center gap-2 pt-2">
                <Button
                  variant="primary"
                  onClick={handleApplyChanges}
                  disabled={applyingChanges || !isFormValid}
                  className="px-3 py-1.5 text-xs text-white rounded font-medium"
                >
                  {applyingChanges ? 'Applying...' : 'Apply Changes'}
                </Button>
                <button
                  onClick={handleResetEdits}
                  className="px-3 py-1.5 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-medium"
                >
                  Reset
                </button>
              </div>
            </div>
          )}

          <iframe
            srcDoc={previewHtml}
            className="w-full h-[600px] bg-white"
            title="Document Preview"
            sandbox="allow-same-origin"
          />
        </div>
      )}
    </div>
  );
}

function DocButton({
  label,
  icon: Icon,
  loading,
  onClick,
}: {
  label: string;
  icon: any;
  loading: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="flex items-center gap-2 px-3 py-2.5 bg-white border border-gray-200 rounded-lg
                 text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50"
    >
      {loading ? (
        <Spinner size="lg" className="w-4 h-4 animate-spin" />
      ) : (
        <Icon className="w-4 h-4 text-gray-400" />
      )}
      {label}
    </button>
  );
}
